import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { resumeSchema } from '@/Pages/Resume/validationSchemas';
import debounce from 'lodash/debounce';
import { usePage } from '@inertiajs/react';
import { FaUndo } from 'react-icons/fa';

export const DataContext = createContext();

// Define default sections in the correct order, including Projects permanently
const defaultSections = [
  { id: 'work_experience', title: 'Work Experience' },
  { id: 'education', title: 'Education' },
  { id: 'projects', title: 'Projects' },
  { id: 'volunteer_experience', title: 'Volunteer Experience' },
  {
    id: 'certifications_skills_interests',
    title: 'Certifications, Skills & Interests',
  },
];

const allPossibleSections = [...defaultSections]; // allPossibleSections is now just defaultSections

const defaultPdfPrintScale = 1;
const defaultSelectedFont = 'Garamond';

export const DataProvider = ({ children }) => {
  const { props, component } = usePage(); // Accessing page props
  const [canUndo, setCanUndo] = useState(false); // Track if there are changes to undo

  const [resumeSettings, setResumeSettings] = useState(() => {
    const stored = localStorage.getItem('resumeSettings');
    const parsedStored = stored ? JSON.parse(stored) : {};

    if (parsedStored?.pdfPrintScale !== 1 && parsedStored?.isAutoScale !== 'false') {
      parsedStored.pdfPrintScale = 1;
    }

    return parsedStored;
  });

  const setResumeSetting = (key, value) => {
    setResumeSettings((prev) => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('resumeSettings', JSON.stringify(updated));
      return updated;
    });
  };

  // Resumes getting loaded from our DB have nulls instead of '' which can cause issue - this cleans up the crap
  const sanitizeTextArrays = (startingData) => {
    let sanitizedData = {
      ...startingData,
    };

    if (startingData.projects && startingData.projects.length > 0) {
      const sanitizedProjects = startingData.projects.map((project) => ({
        ...project,
        descriptions: project.descriptions.map((description) => ({
          text: description?.text ?? '', // Ensure description is always a string
          subDescriptions: description.subDescriptions.filter((sub) => sub), // Filters out null, undefined, and empty strings
        })),
      }));

      sanitizedData = {
        ...sanitizedData,
        projects: sanitizedProjects,
      };
    }

    if (startingData.workHistory && startingData.workHistory.length > 0) {
      const sanitizedWorkHistory = startingData.workHistory.map((job) => ({
        ...job,
        descriptions: job.descriptions.map((description) => ({
          text: description?.text ?? '', // Ensure description is always a string
          subDescriptions: description.subDescriptions?.filter((sub) => sub) || [], // Filters out null, undefined, and empty strings
        })),
      }));

      sanitizedData = {
        ...sanitizedData,
        workHistory: sanitizedWorkHistory,
      };
    }

    if (startingData.volunteerExperiences && startingData.volunteerExperiences.length > 0) {
      const sanitizedVolunteerExperiences = startingData.volunteerExperiences.map((job) => ({
        ...job,
        descriptions: job.descriptions.map((description) => ({
          text: description?.text ?? '', // Ensure description is always a string
          subDescriptions: description.subDescriptions?.filter((sub) => sub) || [], // Filters out null, undefined, and empty strings
        })),
      }));

      sanitizedData = {
        ...sanitizedData,
        volunteerExperiences: sanitizedVolunteerExperiences,
      };
    }

    return sanitizedData;
  };

  const [data, setData] = useState(() => {
    let initialData = {
      contactDetails: {},
      workHistory: [],
      education: [],
      certifications: [],
      interests: [],
      skills: [],
      technologies: [],
    };

    const storedData = JSON.parse(localStorage.getItem('resumeData'));

    if (storedData) {
      initialData = sanitizeTextArrays(storedData);
    }

    return initialData;
  });

  const submitBug = (feedback) => {
    axios
      .post(route('feedback.store'), {
        user_id: props.auth.user ? props.auth.user.id : null,
        want_response: true,
        email: data.contactDetails.email || '',
        feedback: 'SYSTEM-GENERATED-FEEDBACK: ' + feedback, // Accepting dynamic feedback
        include_diagnostics: true,
        localstorage: localStorage.getItem('resumeData'),
      })
      .then((response) => {
        console.log('Bug report submitted successfully:', response.data);
      })
      .catch((error) => {
        console.error('Error submitting bug report:', error);
      });
  };

  const isDataEmpty =
    !data ||
    (Object.values(data.contactDetails || {}).every((value) => !value) &&
      !data.workHistory?.length &&
      !data.education?.length &&
      !data.certifications?.length &&
      !data.interests?.length &&
      !data.skills?.length &&
      !data.technologies?.length &&
      !data.projects?.length &&
      !data.volunteerExperiences?.length &&
      !data.languages?.length);

  const [stepperContextStep, setStepperContextStep] = useState(0);

  const [resumeSaved, setResumeSaved] = useState(null);
  const isInitialLoad = useRef(true);

  const [validationErrors, setValidationErrors] = useState({});
  const [undoInfo, setUndoInfo] = useState([]);
  const latestUndoId = useRef(null);

  // Get resume_id from localStorage
  const [resumeId, setResumeId] = useState(
    localStorage.getItem('resumeId') ||
      (props.auth && props.auth.user && props.auth.user.mostRecentResume && isDataEmpty
        ? props.auth.user.mostRecentResume
        : null)
  );

  const [revisionId, setRevisionId] = useState();

  const [resumeTitle, setResumeTitle] = useState(
    localStorage.getItem('resumeTitle') || 'Name Your Resume'
  ); // State for title

  const [isAutoScale, setIsAutoScale] = useState(
    resumeSettings.isAutoScale === 'false' ? false : true
  );

  const [initialScaleForAutoScale, setInitialScaleForAutoScale] = useState(1);

  useEffect(() => {
    localStorage.setItem('resumeTitle', resumeTitle);
  }, [resumeTitle]);

  // Ref to hold the latest resumeId
  const resumeIdRef = useRef(resumeId);

  // Update the ref whenever resumeId changes
  useEffect(() => {
    resumeIdRef.current = resumeId;

    // Check if undo is available when resumeId changes
    if (resumeId) {
      checkUndoAvailability();
    } else {
      setCanUndo(false);
    }
  }, [resumeId]);

  // Check undo availability when data changes after save
  useEffect(() => {
    if (resumeSaved === 'saved') {
      setTimeout(() => {
        checkUndoAvailability();
      }, 1000); // Give it a second after save completes
    }
  }, [resumeSaved]);

  // Debounced function to update resume in the database
  const debouncedUpdateResume = useRef(
    debounce(async (title, updatedData, settings) => {
      if (!props.auth.user) {
        return;
      }

      const currentResumeId = resumeIdRef.current;

      if (!currentResumeId) {
        console.log('resumeId is missing from localStorage.');
        // Create it
        fetch(`/resumes/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document
              .querySelector('meta[name="csrf-token"]')
              .getAttribute('content'),
          },
          body: JSON.stringify({
            title: title,
            body: updatedData,
            settings: settings,
          }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error('Failed to create resume');
            }
            return response.json();
          })
          .then((data) => {
            setResumeId(data.resume_id);
            toast.success('Resume created.');

            // Set canUndo to false for a new resume (no previous versions yet)
            setCanUndo(false);
          })
          .catch((error) => {
            toast.error('Resume could not be saved!');
          });
        return;
      }

      setResumeSaved('saving');

      try {
        const response = await axios.put(`/resumes/${currentResumeId}`, {
          title: title,
          body: updatedData,
          settings: settings,
        });

        // Handle the response as needed
        if (response.status === 200) {
          setTimeout(() => {
            setResumeSaved('saved');
            setResumeId(response.data.resume.id);

            // After a successful save, check if undo is available
            setCanUndo(true);
          }, 1000);
        } else {
          throw new Error('Failed to update resume');
        }
      } catch (error) {
        setTimeout(() => {
          setResumeSaved('error');
        }, 1000);
        toast.error('Resume could not be saved!');
      }
    }, 5000)
  );

  // Cancel pending debounced calls when the component unmounts
  useEffect(() => {
    return () => {
      debouncedUpdateResume.current.cancel();
    };
  }, []);

  // If resume has never been saved this time, then go ahead and save it (after 5 seconds)
  useEffect(() => {
    if (resumeSaved === null) {
      isInitialLoad.current = false;
      debouncedUpdateResume.current(resumeTitle, data, resumeSettings);
    }

    // Initial check for undo availability
    if (resumeId) {
      checkUndoAvailability();
    }
  }, []);

  // When user makes changes, update the resume in the DB (and localstorage)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    try {
      localStorage.setItem('resumeData', JSON.stringify(data));

      validateData(data).then((errors) => {
        //console.log("ValidationErrors after validation:", errors);
      }); // Validate on data change

      // Trigger the debounced database update
      debouncedUpdateResume.current(resumeTitle, data, resumeSettings);

      // Check if undo is available after changes
      setTimeout(() => {
        checkUndoAvailability();
      }, 100);
    } catch (error) {
      console.error('Failed to save data to local storage', error);
    }
  }, [resumeTitle, data, resumeSettings, debouncedUpdateResume]);

  //Allow user to switch to another saved resume
  const [isLoadingResume, setIsLoadingResume] = useState(false);

  // Keep track of whether we're switching resumes vs. navigating back
  const previousResumeId = useRef(resumeId); // Initialize with the current resumeId
  const isResumeSwitch = useRef(false);

  // Flag to track if we're in the undo process
  const isUndoInProgress = useRef(false);

  // Function to check if undo is available
  const checkUndoAvailability = async () => {
    if (!resumeId) {
      setCanUndo(false);
      return;
    }

    try {
      // Fetch the revision detail
      const revisionResponse = await axios.get(route('revisions', resumeId));

      if (revisionResponse.data && revisionResponse.data.length > 0) {
        // Get the latest revision
        const latestRevision = revisionResponse.data[0];

        try {
          // Fetch the content of this revision
          const revisionDetailResponse = await axios.get(route('revisions.get', latestRevision.id));

          if (revisionDetailResponse.data) {
            // Parse the revision body
            const revisionData = JSON.parse(revisionDetailResponse.data.body);

            // Store the current version data for future comparisons
            setCurrentVersionData(revisionData);

            // Compare with current data
            const currentDataString = JSON.stringify(data);
            const revisionDataString = JSON.stringify(revisionData);

            // If data is different, enable undo
            setCanUndo(currentDataString !== revisionDataString);
          } else {
            // Couldn't get revision detail, enable undo to be safe
            setCanUndo(true);
          }
        } catch (detailError) {
          console.error('Error fetching revision detail:', detailError);
          // If we can't check details, enable undo to be safe
          setCanUndo(true);
        }
      } else {
        // No revisions available
        setCanUndo(false);
      }
    } catch (error) {
      console.error('Error checking undo availability:', error);
      setCanUndo(false);
    }
  };

  // Store the current version data for comparison
  const [currentVersionData, setCurrentVersionData] = useState(null);

  // Function to undo to the previous version of the resume
  const undoToPreviousVersion = async () => {
    if (!resumeId) {
      toast.info('Nothing to undo - no saved resume found');
      return;
    }

    setIsLoadingResume(true);
    // Set the flag to indicate we're in an undo operation
    isUndoInProgress.current = true;

    try {
      // Fetch the list of revisions
      const response = await axios.get(route('revisions', resumeId));

      if (response.data && response.data.length > 0) {
        // Get the most recent revision
        const latestRevision = response.data[0];

        // Only show one toast when undoing
        toast.info('Undoing to previous version...');

        // Set the revision ID to trigger the restore process
        setRevisionId(latestRevision.id);

        // Disable undo button after undoing
        setCanUndo(false);

        // Cancel any pending auto-save
        debouncedUpdateResume.current.cancel();
      } else {
        toast.info('No previous versions found to undo to');
        setIsLoadingResume(false);
        isUndoInProgress.current = false;
        setCanUndo(false);
      }
    } catch (error) {
      console.error('Error fetching revisions for undo:', error);
      toast.error('Failed to undo to previous version');
      setIsLoadingResume(false);
      isUndoInProgress.current = false;
      setCanUndo(false);
    }
  };

  // Update the ref whenever we're explicitly switching resumes
  useEffect(() => {
    if (resumeId && resumeId !== previousResumeId.current) {
      isResumeSwitch.current = true;
      previousResumeId.current = resumeId;
    }
  }, [resumeId]);

  useEffect(() => {
    // Skip if not on the Resume component
    if (component !== 'Resume') {
      return;
    }

    // Skip on initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // Skip if not authenticated
    if (!props.auth.user) {
      return;
    }

    // Function to load resume data
    const loadResumeData = () => {
      return axios.get(`/resumes/${resumeId}`).then((response) => {
        const dbData = response.data;

        if (dbData) {
          // Process and set the resume data
          const parsedData = JSON.parse(dbData.body);
          const sanitizedData = sanitizeTextArrays(parsedData);

          setData(sanitizedData);
          setResumeTitle(dbData.title);

          // Load resume settings safely even if malformed
          try {
            const loadedResumeSettings = JSON.parse(dbData.settings);

            const savedReorderableSections =
              loadedResumeSettings?.reorderableSections &&
              JSON.parse(loadedResumeSettings.reorderableSections);

            const formattedSavedReorderableSections = savedReorderableSections
              ?.map((id) => allPossibleSections.find((section) => section.id === id))
              .filter(Boolean);

            setSections(
              formattedSavedReorderableSections?.length > 0
                ? formattedSavedReorderableSections
                : defaultSections
            );

            updateIsAutoScale(loadedResumeSettings?.isAutoScale === 'false' ? false : true);

            const shouldSetPdfPrintScale =
              loadedResumeSettings?.pdfPrintScale && loadedResumeSettings?.isAutoScale === 'false';

            setPdfPrintScale(
              shouldSetPdfPrintScale ? loadedResumeSettings.pdfPrintScale : defaultPdfPrintScale
            );
            setSelectedFont(loadedResumeSettings?.selectedFont || defaultSelectedFont);
          } catch (error) {
            console.error('Error parsing resume settings:', error);
            setSections(defaultSections);
          }

          // Update localStorage with current resumeId
          localStorage.setItem('resumeId', resumeId);

          return dbData;
        }

        return null;
      });
    };

    // Check if resumeId changed OR if revisionId exists
    if (resumeId && (resumeId !== localStorage.getItem('resumeId') || revisionId)) {
      setIsLoadingResume(true);

      // If revisionId exists, first restore the revision, then load the resume
      if (revisionId) {
        axios
          .post(route('revisions.restore', revisionId))
          .then(() => {
            // Only show toast if not in an undo operation
            if (!isUndoInProgress.current) {
              toast.success('Revision restored successfully');
            }
            setRevisionId(null);
            // After restoring, load the resume data
            return loadResumeData();
          })
          .then((dbData) => {
            if (dbData) {
              // Only show toast if not in an undo operation or if explicitly switching
              if (!isUndoInProgress.current && isResumeSwitch.current) {
                toast.success(`Resume updated: ${dbData.title}`);
              }
            }
            setIsLoadingResume(false);
            // Reset the undo flag after restoration is complete
            isUndoInProgress.current = false;

            // Check if undo is still available
            checkUndoAvailability();
          })
          .catch((error) => {
            console.error('Error restoring revision or loading resume:', error);
            // Always show errors
            toast.error('Failed to restore revision');
            setIsLoadingResume(false);
            isUndoInProgress.current = false;
          });
      } else {
        // Just load the resume without revision handling
        loadResumeData()
          .then((dbData) => {
            if (dbData) {
              // Only show toast when EXPLICITLY switching between resumes (from the open modal)
              // not when navigating away and back to the builder,
              // and not during an undo operation
              if (isResumeSwitch.current && !isUndoInProgress.current) {
                toast.success(`Switched to resume: ${dbData.title}`);
                isResumeSwitch.current = false;
              }
            }
            setIsLoadingResume(false);
            isUndoInProgress.current = false;
          })
          .catch((error) => {
            console.error('Error loading resume:', error);
            toast.error('Failed to load resume');
            setIsLoadingResume(false);
            isUndoInProgress.current = false;
          });
      }
    }
  }, [resumeId, revisionId, component, props.auth.user]);

  const validateData = async (dataToValidate) => {
    try {
      await resumeSchema.validate(dataToValidate, {
        context: { skipEmailValidation: resumeSettings.hideEmailError },
        abortEarly: false,
      });
      setValidationErrors({});
      return {};
    } catch (errors) {
      const formattedErrors = errors.inner.reduce((acc, error) => {
        const pathParts = error.path.split(/[\[\]\.]/).filter(Boolean);

        pathParts.reduce((accumulator, part, index) => {
          if (index === pathParts.length - 1) {
            accumulator[part] = error.message;
          } else {
            if (!accumulator[part]) {
              accumulator[part] = {};
            }
            return accumulator[part];
          }
        }, acc);

        return acc;
      }, {});

      setValidationErrors(formattedErrors);
      return formattedErrors;
    }
  };

  const getErrorCountForSection = (section) => {
    const traverseErrors = (obj) => {
      let count = 0;
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (typeof obj[key] === 'object') {
            count += traverseErrors(obj[key]);
          } else {
            count += 1;
          }
        }
      }
      return count;
    };

    return traverseErrors(validationErrors[section] || {});
  };

  // Initialize sections from localStorage or fallback to defaultSections
  const [sections, setSections] = useState(() => {
    const savedSectionOrder = resumeSettings.reorderableSections;

    if (savedSectionOrder) {
      const savedOrder = Array.isArray(savedSectionOrder)
        ? savedSectionOrder
        : JSON.parse(savedSectionOrder);

      // Map saved IDs to actual section objects
      return savedOrder.map((id) => defaultSections.find((section) => section.id === id));
    }
    return defaultSections;
  });

  useEffect(() => {
    // Map sections to section IDs
    let sectionOrder = sections.map((section) => section.id);

    // Ensure "projects" is included right after "education"
    if (!sectionOrder.includes('projects')) {
      const updatedSectionOrder = [];

      sectionOrder.forEach((section) => {
        updatedSectionOrder.push(section);
        if (section === 'education') {
          updatedSectionOrder.push('projects');
        }
      });

      sectionOrder = updatedSectionOrder;
    }

    // Ensure "volunteer_experience" is included right after "projects"
    if (!sectionOrder.includes('volunteer_experience')) {
      const updatedSectionOrder = [];

      sectionOrder.forEach((section) => {
        updatedSectionOrder.push(section);
        if (section === 'projects') {
          updatedSectionOrder.push('volunteer_experience');
        }
      });

      sectionOrder = updatedSectionOrder;
    }

    // Save to localStorage
    setResumeSetting('reorderableSections', JSON.stringify(sectionOrder));
  }, [sections]);

  // Function to update sections
  const updateSections = (newSections) => {
    setSections(newSections);
  };

  const [highlightExportArea, setHighlightExportArea] = useState(false);

  // Load pdfPrintScale from localStorage or fallback to default value 1
  const [pdfPrintScale, setPdfPrintScale] = useState(() => {
    const savedScale = resumeSettings.pdfPrintScale;
    return savedScale ? savedScale : defaultPdfPrintScale;
  });

  // Whenever pdfPrintScale changes, save it to localStorage
  useEffect(() => {
    setResumeSetting('pdfPrintScale', pdfPrintScale);
  }, [pdfPrintScale]);

  // const [finalPdfPrintScale, setFinalPdfPrintScale] = useState(pdfPrintScale); // Updated state

  // const adjustingScaleRef = useRef(false); // To prevent concurrent adjustments

  const updateIsAutoScale = (value) => {
    setIsAutoScale(value);
    setResumeSetting('isAutoScale', value ? 'true' : 'false'); // Save to localStorage
  };

  // useEffect(() => {
  //   if (isAutoScale) {
  //     let currentScale = finalPdfPrintScale; // Use finalPdfPrintScale

  //     const adjustScale = () => {
  //       if (previewHeight > pageBreakTrigger && currentScale > -4) {
  //         currentScale -= 1;
  //         setFinalPdfPrintScale(currentScale);
  //       } else if (previewHeight < pageBreakTrigger && currentScale < 4) {
  //         currentScale += 1;
  //         setFinalPdfPrintScale(currentScale);
  //       } else {
  //         // Exit condition met
  //         adjustingScaleRef.current = false; // Allow future adjustments
  //       }
  //     };

  //     adjustScale();
  //   } else {
  //     setFinalPdfPrintScale(pdfPrintScale);
  //   }
  // }, [pdfPrintScale, isAutoScale]);

  // New font selection management
  const [selectedFont, setSelectedFont] = useState(() => {
    const savedFont = resumeSettings.selectedFont;
    return savedFont || defaultSelectedFont; // Default to "Garamond"
  });

  // Update localStorage whenever font changes
  useEffect(() => {
    setResumeSetting('selectedFont', selectedFont);
  }, [selectedFont]);

  // New tailoring set
  const [tailoring, setTailoring] = useState(() => {
    const savedTailoring = resumeSettings.tailoring;
    return savedTailoring || ''; // Default to "Garamond"
  });

  useEffect(() => {
    setResumeSetting('tailoring', tailoring);
  }, [tailoring]);

  const showUndoToast = (id) => {
    toast(
      <div>
        Item deleted.
        <button
          onClick={(e) => {
            undoDelete(id);
            e.stopPropagation(); // Prevent click from propagating to the modal
            //console.log(`Undo button clicked for id: ${id}`);
          }}
          style={{
            marginLeft: '10px',
            color: 'blue',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: '0',
          }}
        >
          Undo
        </button>
      </div>,
      {
        toastId: id,
        autoClose: 5000, // 5 seconds
      }
    );
  };

  const reorderItems = (list, startIndex, endIndex) => {
    const filteredList = list.filter((item) =>
      typeof item === 'string' ? item.trim() !== '' : true
    ); // Remove empty strings if item is a string
    const result = Array.from(filteredList);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const reorderJobs = (startIndex, endIndex) => {
    setData((prevData) => ({
      ...prevData,
      workHistory: reorderItems(prevData.workHistory, startIndex, endIndex),
    }));
  };

  const reorderEducations = (startIndex, endIndex) => {
    setData((prevData) => ({
      ...prevData,
      education: reorderItems(prevData.education, startIndex, endIndex),
    }));
  };

  const reorderProjects = (startIndex, endIndex) => {
    setData((prevData) => ({
      ...prevData,
      projects: reorderItems(prevData.projects, startIndex, endIndex),
    }));
  };

  const reorderVolunteerExperiences = (startIndex, endIndex) => {
    setData((prevData) => ({
      ...prevData,
      volunteerExperiences: reorderItems(prevData.volunteerExperiences, startIndex, endIndex),
    }));
  };

  const reorderDescriptions = (section, itemIndex, startIndex, endIndex) => {
    setData((prevData) => {
      const newSection = [...prevData[section]];
      newSection[itemIndex].descriptions = reorderItems(
        newSection[itemIndex].descriptions,
        startIndex,
        endIndex
      );
      return { ...prevData, [section]: newSection };
    });
  };

  const reorderSubDescriptions = (section, itemIndex, descIndex, startIndex, endIndex) => {
    setData((prevData) => {
      const newSection = [...prevData[section]];
      newSection[itemIndex].descriptions[descIndex].subDescriptions = reorderItems(
        newSection[itemIndex].descriptions[descIndex].subDescriptions,
        startIndex,
        endIndex
      );
      return { ...prevData, [section]: newSection };
    });
  };

  const deleteItem = (section, index, subIndex, subSubIndex) => {
    const isEmpty = (value) => {
      if (value === undefined || value === null) return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      if (Array.isArray(value)) {
        return value.length === 0 || value.every(isEmpty);
      }
      if (typeof value === 'object') {
        return Object.keys(value).length === 0 || Object.values(value).every(isEmpty);
      }
      return false;
    };

    const isItemEmpty = (item) => {
      if (!item) return true;

      if (typeof item === 'string' && item.trim() !== '') {
        return false;
      }

      return Object.entries(item).every(([key, value]) => isEmpty(value));
    };

    const description = data?.[section]?.[index]?.descriptions?.[subIndex];
    const subDescription =
      data?.[section]?.[index]?.descriptions?.[subIndex]?.subDescriptions?.[subSubIndex];

    let newSectionData = [...data[section]];
    let deletedItem;

    if (subIndex !== undefined && subSubIndex !== undefined) {
      deletedItem = newSectionData[index].descriptions[subIndex].subDescriptions.splice(
        subSubIndex,
        1
      )[0];
    } else if (subIndex !== undefined) {
      deletedItem = newSectionData[index].descriptions.splice(subIndex, 1)[0];
    } else {
      deletedItem = newSectionData.splice(index, 1)[0];
    }
    setData({ ...data, [section]: newSectionData });

    const id = Date.now();
    const newUndoInfo = [
      ...undoInfo,
      { id, section, index, subIndex, subSubIndex, item: deletedItem },
    ];
    setUndoInfo(newUndoInfo);
    latestUndoId.current = id;

    if (
      !isItemEmpty(subDescription ?? description ?? data?.[section]?.[index] ?? data?.[section])
    ) {
      showUndoToast(id);
    }
  };

  const undoDelete = (id) => {
    setUndoInfo((prevUndoInfo) => {
      const info = prevUndoInfo.find((undo) => undo.id === id);
      if (info) {
        let newSectionData = [...data[info.section]];

        if (info.subIndex !== undefined && info.subSubIndex !== undefined) {
          newSectionData[info.index].descriptions[info.subIndex].subDescriptions.splice(
            info.subSubIndex,
            1,
            info.item
          );
        } else if (info.subIndex !== undefined) {
          newSectionData[info.index].descriptions.splice(info.subIndex, 0, info.item);
        } else {
          newSectionData.splice(info.index, 1, info.item);
        }

        setData({ ...data, [info.section]: newSectionData });
        toast.dismiss(id);
        return prevUndoInfo.filter((undo) => undo.id !== id);
      } else {
        toast.error(`No undo info found for id: ${id}`);
        return prevUndoInfo;
      }
    });
  };

  const finishedSteps = (() => {
    const steps = [];

    if (data.contactDetails && data.contactDetails.fullName) {
      steps.push('Contact Details');
    }
    if (data.workHistory && data.workHistory.length > 0) {
      steps.push('Work History');
    }
    if (data.education && data.education.length > 0) {
      steps.push('Education');
    }
    if (data.certifications && data.certifications.length > 0) {
      steps.push('Certifications');
    }
    if (data.interests && data.interests.length > 0) {
      steps.push('Interests');
    }
    if (data.skills && data.skills.length > 0) {
      steps.push('Skills');
    }

    return steps;
  })();

  const isDegree = (text) => {
    const degreesStartWith = [
      'Associate',
      'Associates',
      'Masters',
      'Bachelor',
      'Master',
      'Doctorate',
      'PhD',
      'PHD',
      'Phd',
      'MBA',
      'Mba',
      'BSc',
      'Bsc',
      'BA',
      'MSc',
      'MA',
      'B.A',
      'BBA',
      'BAA',
      'High School Diploma',
      'GED',
      'G.E.D.',
      'B.S',
      'Certificate',
      // Add more degrees as needed
    ];

    const degreesContains = [
      'Diploma',
      'Associate',
      'Bachelor',
      'Master',
      'PhD',
      'PHD',
      'Phd',
      'Degree',
      // Add more keywords as needed
    ];

    if (text === null) {
      return false;
    }
    // Convert text to lower case for case insensitive comparison
    const lowerText = text.toLowerCase();

    // Check if the text starts with any of the degree terms
    const startsWithDegree = degreesStartWith.some((degree) =>
      lowerText.startsWith(degree.toLowerCase())
    );

    // Check if the text contains any of the keywords (Diploma, Associate, etc.)
    const containsDegree = degreesContains.some((keyword) =>
      lowerText.includes(keyword.toLowerCase())
    );

    return startsWithDegree || containsDegree;
  };

  const extractLastYear = (text) => {
    const yearRangeRegex = /\b(\d{4})\b(?:\s*[-–]\s*\b(\d{4})\b)?$/;
    const match = text.match(yearRangeRegex);

    if (match) {
      return match[2] || match[1]; // Return the second year if present, otherwise the first year
    }

    return null; // Return null if no match is found
  };

  const formatEducationDateStartDateEnd = (dateStartDateEnd, isDegree) => {
    /*if (isDegree) {
      return extractLastYear(dateStartDateEnd);
    }*/
    return dateStartDateEnd;
  };

  // Method to loop through work history and calculate total years worked
  const calculateTotalYearsWorked = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    let totalYearsWorked = 0;

    const monthMapping = {
      January: 0,
      February: 1,
      March: 2,
      April: 3,
      May: 4,
      June: 5,
      July: 6,
      August: 7,
      September: 8,
      October: 9,
      November: 10,
      December: 11,
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    const parseDate = (dateStr, isEndDate = false) => {
      if (!dateStr) {
        return dateStr;
      }
      if (dateStr.match(/Present|Current|Now/i)) {
        return currentDate;
      }

      const monthRegex =
        /(January|February|March|April|May|June|July|August|September|October|November|December|Jan\.?|Feb\.?|Mar\.?|Apr\.?|May\.?|Jun\.?|Jul\.?|Aug\.?|Sep\.?|Oct\.?|Nov\.?|Dec\.?)/i;
      const yearRegex = /\d{4}/;
      const monthMatch = dateStr.match(monthRegex);
      const yearMatch = dateStr.match(yearRegex);

      let year = yearMatch ? parseInt(yearMatch[0], 10) : currentYear;
      let month = monthMatch ? monthMapping[monthMatch[0]] : isEndDate ? 11 : 0;

      return new Date(year, month, 1);
    };

    data.workHistory.forEach((job) => {
      if (job.yearStartYearEnd && job.yearStartYearEnd.trim() !== '') {
        const dateRange = job.yearStartYearEnd;
        const dateParts = dateRange.split(/\s*[-–—]\s*/);
        const startDate = parseDate(dateParts[0]);
        const endDate = parseDate(dateParts[1], true);

        if (startDate && endDate) {
          const yearsWorked = endDate.getFullYear() - startDate.getFullYear();
          const monthsWorked = endDate.getMonth() - startDate.getMonth();
          const totalWorked = yearsWorked + monthsWorked / 12;

          totalYearsWorked += Math.ceil(totalWorked);
        }
      }
    });

    return totalYearsWorked;
  };

  const totalYearsWorked = useMemo(calculateTotalYearsWorked, [data.workHistory]);

  const [pageBreakTrigger, setPageBreakTrigger] = useState(670); // Default value
  const [previewHeight, setPreviewHeight] = useState(750); // Initial value
  const [clickedPreviewItem, setClickedPreviewItem] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // Step 0 corresponds to "Contact Details"

  return (
    <DataContext.Provider
      value={{
        data,
        setData,
        isDataEmpty,
        validationErrors,
        setValidationErrors,
        getErrorCountForSection,
        isDegree,
        formatEducationDateStartDateEnd,
        finishedSteps,
        reorderJobs,
        reorderEducations,
        reorderProjects,
        reorderVolunteerExperiences,
        reorderDescriptions,
        reorderSubDescriptions,
        deleteItem,
        undoDelete,
        setUndoInfo,
        latestUndoId,
        totalYearsWorked,
        stepperContextStep,
        setStepperContextStep,
        pdfPrintScale,
        isLoadingResume,
        isAutoScale, // Updated context value
        setPdfPrintScale,
        updateIsAutoScale, // Updated update function
        initialScaleForAutoScale,
        setInitialScaleForAutoScale,
        // finalPdfPrintScale,
        sections,
        updateSections,
        defaultSections,
        resumeSettings,
        setResumeSetting,
        selectedFont,
        setSelectedFont,
        tailoring,
        setTailoring,
        pageBreakTrigger, // Expose pageBreakTrigger to context
        setPageBreakTrigger, // Function to modify pageBreakTrigger
        clickedPreviewItem,
        setClickedPreviewItem,
        currentStep,
        setCurrentStep,
        resumeTitle,
        setResumeTitle,
        resumeId,
        setResumeId,
        highlightExportArea,
        setHighlightExportArea,
        resumeSaved,
        setResumeSaved,
        submitBug,
        previewHeight,
        setPreviewHeight,
        setRevisionId,
        undoToPreviousVersion,
        checkUndoAvailability,
        canUndo,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
